<template>
  <MessageComponent v-if="!filterStore.currentPortfolio" color="info">
    Kies een portfolio om berichten te bekijken.
  </MessageComponent>
  <MessageComponent
    v-else-if="!contactStore.loadingTickets && !rows.length"
    color="info"
  >
    Er zijn geen berichten voor dit portfolio.
  </MessageComponent>
  <BaseCard v-else class="overflow-auto">
    <LoadingWrapper
      :loading="contactStore.loadingTickets"
      :error="contactStore.loadingTicketsFailed"
    >
      <BaseTable :columns="columns" :rows="rows">
        <template #status-cell="{ row }">
          <BaseBadge :color="statusBadge(row.status).color">{{
            statusBadge(row.status).label
          }}</BaseBadge>
        </template>
      </BaseTable>
    </LoadingWrapper>
  </BaseCard>
</template>

<script setup>
import { computed, watch } from "vue"
import { format } from "date-fns"
import toDate from "@/helpers/toDate.js"
import {
  formatWeekdaysString,
  formatHoursString,
} from "@/helpers/timeFormatting.js"
import useFilterStore from "@/stores/filterStore.js"
import useNotificationStore from "@/stores/notificationStore.js"
import useContactStore from "@/stores/repower/contactStore.js"
import useUserStore from "@/stores/userStore.js"
import useMeteringPointsStore from "@/stores/repower/meteringPointsStore.js"
import MessageComponent from "@/components/information/MessageComponent.vue"
import {
  BaseTable,
  BaseCard,
  BaseBadge,
  LoadingWrapper,
} from "@repowerednl/ui-component-library"

const LOAD_TICKETS_ERROR_KEY = "load-my-tickets-error"
const TYPE_MAP = {
  general: "Algemene vraag",
  disruption: "Storing of tijdelijke wijziging",
}

const filterStore = useFilterStore()
const notificationStore = useNotificationStore()
const contactStore = useContactStore()
const userStore = useUserStore()
const meteringPointsStore = useMeteringPointsStore()

const columns = {
  createdAt: { label: "Aangemaakt op" },
  type: { label: "Type bericht" },
  meteringPoint: { label: "Meetpunt" },
  dateStart: { label: "Startdatum" },
  dateEnd: { label: "Einddatum" },
  recurring: {
    label: "Tijdvakken",
    classes: "whitespace-pre",
  },
  status: { label: "Status" },
}

/**
 * Outputs a string representation of the recurring days and hour specification.
 * Example output: "
 *   ma, di, vr, za
 *   10:00 - 15:00, 20:00 - 21:00
 * "
 *
 * @param weekdays Array of weekdays (0-6) on which the event recurs
 * @param hours Array of hours (0-23) during which the event occurs
 */
function getRecurringTimeString(weekdays, hours) {
  const lines = []

  const weekdaysStr = formatWeekdaysString(weekdays)
  if (weekdaysStr) {
    lines.push(weekdaysStr)
  }

  const hoursStr = formatHoursString(hours)
  if (hoursStr) {
    lines.push(hoursStr)
  }

  return lines.join("\n")
}

function normalizeRow(item) {
  const dateStart = toDate(item.date_start)
  const dateEnd = toDate(item.date_end)
  const createdAt = toDate(item.created_at ?? item.created)
  const meteringPoint = meteringPointsStore.findMeteringPoint(
    item.metering_point_id,
  )?.displayName

  return {
    _id: "cell",
    createdAtValue: createdAt?.getTime() ?? 0,
    createdAt: createdAt ? format(createdAt, "dd-MM-yyyy HH:mm") : "-",
    type: TYPE_MAP[item.type] ?? item.type,
    meteringPoint: meteringPoint ?? "-",
    dateStart: dateStart ? format(dateStart, "dd-MM-yyyy") : "-",
    dateEnd: dateEnd ? format(dateEnd, "dd-MM-yyyy") : "-",
    recurring: getRecurringTimeString(item.weekdays, item.hours) || "-",
    status: item.status,
  }
}

const rows = computed(() => contactStore.tickets.map(normalizeRow))

function statusBadge(status) {
  switch (status) {
    case "open":
      return { color: "gray", label: "Open" }
    case "in_progress":
      return { color: "info", label: "In behandeling" }
    case "closed":
      return { color: "success", label: "Afgerond" }
    default:
      return { color: "gray", label: "Onbekend" }
  }
}

watch(
  [() => filterStore.currentPortfolio?.id, () => userStore.id],
  async ([portfolioId, userId]) => {
    if (!portfolioId || !userId) {
      contactStore.resetTickets()
      return
    }

    try {
      await contactStore.loadTickets(portfolioId)
    } catch (error) {
      const code = error?.code ?? "Unknown"
      notificationStore.pushError(
        "Fout bij het ophalen van berichten",
        `De berichten konden niet worden opgehaald. Probeer het later opnieuw. (code: ${code})`,
        LOAD_TICKETS_ERROR_KEY,
      )
    }
  },
  { immediate: true },
)
</script>
